'use client';

import { forwardRef, useState } from 'react';
import useShortcut from '../../hooks/use-shortcut';
import type { ButtonSize, ButtonVariant } from '../button/button.types';
import type { Option } from './components/dropdown-options';
import DropdownOptions from './components/dropdown-options';
import MainButton from './components/main-button';
export type SplitButtonProps = {
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  variant?: ButtonVariant;
  options: Option[];
  size?: ButtonSize;
  /**
   * When true (default), picking an option from the dropdown both updates the
   * main button and fires `onSelect` immediately — split-menu pattern.
   * When false, dropdown selection only updates the main button; the user must
   * click the main button to execute — standard split-button pattern.
   */
  executeOnSelect?: boolean;
  /**
   * When provided, the active option is persisted in `localStorage` under this
   * key, so the user's last selection survives navigation and reloads.
   */
  persistKey?: string;
};
export const triggerWidths: Record<ButtonSize, string> = {
  compact: '28px',
  default: '36px',
  large: '44px'
};
const SplitButton = forwardRef<HTMLButtonElement, SplitButtonProps>(({
  disabled = false,
  loading = false,
  type = 'button',
  variant = 'secondary',
  size = 'default',
  options,
  executeOnSelect = true,
  persistKey
}: SplitButtonProps, ref) => {
  const [activeOption, setActiveOption] = useState<Option>(() => {
    if (typeof window === 'undefined' || !persistKey) return options[0];
    try {
      const saved = window.localStorage.getItem(persistKey);
      const match = saved ? options.find(o => o.value === saved) : null;
      return match ?? options[0];
    } catch {
      return options[0];
    }
  });

  // TODO: Add a way to handle multiple shortcuts for unknown number of options -- today this is fine because split button only has 2 options
  useShortcut({
    keys: options[0].shortcut || [],
    onShortcut: options[0].onSelect,
    disabled: !!options[0].disabled || disabled
  });
  useShortcut({
    keys: options[1].shortcut || [],
    onShortcut: options[1].onSelect,
    disabled: !!options[1].disabled || disabled
  });
  return <div className="fp-split-button" data-size={size} data-variant={variant}>
        <MainButton disabled={disabled} loading={loading} loadingAriaLabel="Loading" onClick={activeOption.onSelect} ref={ref} size={size} tab-index={0} type={type} variant={variant}>
          {activeOption.label}
        </MainButton>
        <DropdownOptions options={options} loading={loading} disabled={disabled} variant={variant} size={size} onOptionChange={(option: Option) => {
      setActiveOption(option);
      if (persistKey && typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(persistKey, option.value);
        } catch {
          // Storage may be unavailable (private mode, quota); ignore.
        }
      }
      if (executeOnSelect) option.onSelect();
    }} width={triggerWidths[size]} tab-index={1} />
      </div>;
});
export default SplitButton;